// src/utils/authEmail.js
// Helpers for Firebase Auth emails (verification + password reset) and
// user-friendly auth error messages.
import { sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';

// Must match Firebase Auth > Settings > Authorized domains.
const AUTHORIZED_HOSTS = [
  'www.thewastegame.co.uk',
  'thewastegame.co.uk',
  'www.thewastegame.com',
  'thewastegame.com',
];
const DEFAULT_ORIGIN = 'https://www.thewastegame.com';

// Continue URL shown after the user clicks the link in the email.
// Use the current site if it is an authorized domain, otherwise the canonical host.
export const continueUrl = (path) => {
  let origin = DEFAULT_ORIGIN;
  try {
    if (AUTHORIZED_HOSTS.includes(window.location.hostname)) origin = window.location.origin;
  } catch (_) { /* non-browser */ }
  return `${origin}${path}`;
};

export const sendVerificationEmail = (firebaseUser) =>
  sendEmailVerification(firebaseUser, { url: continueUrl('/verify-email'), handleCodeInApp: false });

export const sendResetEmail = (email) =>
  sendPasswordResetEmail(auth, email, { url: continueUrl('/login'), handleCodeInApp: false });

// Turn Firebase error codes into friendly messages
export const friendlyAuthError = (codeOrErr) => {
  const code = typeof codeOrErr === 'string' ? codeOrErr : codeOrErr?.code;
  switch (code) {
    case 'auth/email-already-in-use':     return 'That email is already registered. Try logging in.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists for this email address with a password. Sign in with your email and password. Signing in with the same email links Google to that account.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':  return 'Google sign-in was closed before it finished.';
    case 'auth/popup-blocked':            return 'The browser blocked the Google sign-in window. Allow pop-ups for this site and try again.';
    case 'auth/unauthorized-domain':      return 'Google sign-in is not available from this website address.';
    case 'auth/invalid-email':            return 'Please enter a valid email address.';
    case 'auth/missing-email':            return 'Please enter your email address.';
    case 'auth/weak-password':            return 'Password must be at least 6 characters.';
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/user-not-found':
    case 'auth/wrong-password':           return 'Incorrect email or password. Please try again.';
    case 'auth/user-disabled':            return 'This account has been disabled. Contact info@thewastegame.co.uk for help.';
    case 'auth/too-many-requests':        return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/network-request-failed':   return 'Network error - check your internet connection and try again.';
    case 'auth/unauthorized-continue-uri':
    case 'auth/invalid-continue-uri':     return 'Email link configuration problem. Please contact info@thewastegame.co.uk.';
    case 'auth/requires-recent-login':    return 'For security, please log out and log back in, then try again.';
    default:                              return 'Something went wrong. Please try again.';
  }
};
